import { z } from "zod";

const UUIDSchema = z.uuid();

/** How confidently we resolved the supplier from an email's `from` address. */
export const SupplierConfidenceSchema = z.enum(["resolved", "unknown"]);

export const DetectedSupplierSchema = z.object({
  supplierId: UUIDSchema.nullable(),
  supplierName: z.string().nullable(),
  confidence: SupplierConfidenceSchema,
  hasParseConfig: z.boolean(),
});

/** One named contribution to the is-an-849 score, so the UI can explain it. */
export const Email849ScoreSignalSchema = z.object({
  key: z.enum([
    "sender-unresolved",
    "sender-resolved",
    "parser-configured",
    "has-attachments",
    "internal-subject",
    "existing-849",
    "sender-labeled-849",
    "sender-labeled-not-849",
  ]),
  delta: z.number(),
});

/**
 * An email forwarded AS AN ATTACHMENT onto another one.
 *
 * Not an attachment: Outlook's "forward as attachment" arrives as a Graph
 * `itemAttachment` and ingestion files it as its own inbound email, so it has no
 * `core_documents` row to open or parse. It is a message — you open it, you don't
 * parse it — which is why it travels as its own field everywhere rather than being
 * folded into an attachment list some parser would then choke on.
 */
export const AttachedChildEmailSchema = z.object({
  inboundEmailId: UUIDSchema,
  from: z.string(),
  subject: z.string().nullable(),
  receivedAt: z.coerce.date(),
  /** Its OWN file attachments — stored against the child, not the parent. */
  attachmentCount: z.int().nonnegative(),
});

// ── Reconcile 849 (the engine) ──────────────────────────────────────────────

/** Keep in sync with `EDI849LineResultsStatusSchema` and the `SD849LineResultsStatus` model enum. */
export const SD849LineResultsStatusSchema = z.enum([
  "accepted",
  "rejected",
  "partial",
  "unknown",
  "resubmitted",
  "advisory",
]);

/** An existing 849 response an email may belong to (surfaced for confirmation). */
export const AttachCandidateSchema = z.object({
  responseId: UUIDSchema,
  supplierName: z.string(),
  lineResultsStatus: SD849LineResultsStatusSchema,
  overlappingLineCount: z.int().nonnegative(),
  reason: z.enum(["shared-844-lines", "same-supplier-recent", "same-adm"]),
  createdAt: z.coerce.date(),
});

/** The engine's "here's what I was unsure about" channel. */
export const OpenQuestionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("supplier"), reason: z.string() }),
  z.object({
    kind: z.literal("attach-candidate"),
    candidates: z.array(AttachCandidateSchema),
    /**
     * The one existing 849 the deterministic thread/ADM resolution points at, to be
     * offered PRE-FILLED — null when it abstained.
     *
     * A suggestion and nothing more: per ADR 0004 nothing attaches without a person
     * confirming it, and no model ever picks this value. `.default(null)` because a
     * payload written before this field existed must read as "no suggestion", which
     * is the honest answer for one.
     */
    suggestedResponseId: UUIDSchema.nullable().default(null),
  }),
  z.object({
    kind: z.literal("poor-parse"),
    supplierId: z.string(),
    supplierName: z.string(),
  }),
]);

export const ReconcileOutcomeSchema = z.enum([
  "created",
  "attached",
  "evolved",
  "already-exists",
  "dismissed",
  "needs-input",
]);

export const ReconciliationSchema = z.object({
  outcome: ReconcileOutcomeSchema,
  responseId: UUIDSchema.nullable(),
  supplier: DetectedSupplierSchema,
  parse: z.object({
    lineResultsStatus: SD849LineResultsStatusSchema.nullable(),
    acceptedCount: z.number(),
    rejectedCount: z.number(),
    partialCount: z.number(),
    lineCount: z.number(),
  }),
  match: z.object({ ran: z.boolean(), note: z.string() }),
  openQuestions: z.array(OpenQuestionSchema),
  summary: z.string(),
});

export type SupplierConfidence = z.infer<typeof SupplierConfidenceSchema>;
export type DetectedSupplier = z.infer<typeof DetectedSupplierSchema>;
export type AttachedChildEmail = z.infer<typeof AttachedChildEmailSchema>;
export type AttachCandidate = z.infer<typeof AttachCandidateSchema>;
export type OpenQuestion = z.infer<typeof OpenQuestionSchema>;
export type Reconciliation = z.infer<typeof ReconciliationSchema>;
